/**
 * Dynamic User Dropdown Handler
 * Loads user dropdown content dynamically to avoid caching issues
 */
import {loadAuthState} from './auth-state.js';

let initialized = false;
let setupStarted = false;
let teardown = function() {};

export function init() {
    if (initialized || typeof document === 'undefined') {
        return teardown;
    }

    initialized = true;

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setup, {once: true});
        teardown = function() {
            document.removeEventListener('DOMContentLoaded', setup);
            initialized = false;
            teardown = function() {};
        };
    } else {
        setup();
    }

    return function() {
        teardown();
    };
}

function setup() {
    if (!initialized || setupStarted) {
        return;
    }

    setupStarted = true;

    const userAuthNav = document.getElementById('user-auth-nav');
    const mobileUserIcon = document.getElementById('mobile-user-icon');
    const userDropdownMenu = document.getElementById('user-dropdown-menu');
    const mobileUserDropdownMenu = document.getElementById('mobile-user-dropdown-menu');
    const labelsSource = userAuthNav || mobileUserIcon;
    let isLoggedIn = false;
    let authRequestId = 0;
    let menusRevealed = false;

    function revealMenus() {
        if (menusRevealed) {
            return;
        }

        menusRevealed = true;
        userDropdownMenu?.removeAttribute('hidden');
        mobileUserDropdownMenu?.removeAttribute('hidden');
    }

    function getLabels() {
        const dataset = labelsSource?.dataset || {};

        function resolveLabel(value, fallback, translationKey) {
            if (!value || value === translationKey) {
                return fallback;
            }

            return value;
        }

        return {
            account: resolveLabel(dataset.labelAccount, 'Mijn account', 'AccountNavMyAccount'),
            logout: resolveLabel(dataset.labelLogout, 'Uitloggen', 'AccountNavLogout'),
            login: resolveLabel(dataset.labelLogin, 'Inloggen', 'AccountLoginBtn'),
            register: resolveLabel(dataset.labelRegister, 'Registreer', 'AccountNavRegister'),
            accountUrl: dataset.urlAccount || '/account',
            logoutUrl: dataset.urlLogout || '/logout',
            loginUrl: dataset.urlLogin || '/login',
            registerUrl: dataset.urlRegister || '/register',
        };
    }

    async function checkUserStatus() {
        const currentRequestId = ++authRequestId;
        const nextIsLoggedIn = await loadAuthState();

        if (!initialized || currentRequestId !== authRequestId) {
            return;
        }

        isLoggedIn = nextIsLoggedIn;
        updateDropdownContent();
    }

    function updateDropdownContent() {
        const labels = getLabels();
        const dropdownContent = isLoggedIn
            ? `
            <a href="${labels.accountUrl}" class="user-dropdown-item">
                <svg class="user-icon" viewBox="0 0 448 512" aria-hidden="true"><use href="#user-icon-user"/></svg>
                <span>${labels.account}</span>
            </a>
            <a href="${labels.logoutUrl}" class="user-dropdown-item">
                <svg class="user-icon" viewBox="0 0 512 512" aria-hidden="true"><use href="#user-icon-sign-out"/></svg>
                <span>${labels.logout}</span>
            </a>
        `
            : `
            <a href="${labels.loginUrl}" class="user-dropdown-item">
                <svg class="user-icon" viewBox="0 0 512 512" aria-hidden="true"><use href="#user-icon-sign-in"/></svg>
                <span>${labels.login}</span>
            </a>
            <a href="${labels.registerUrl}" class="user-dropdown-item">
                <svg class="user-icon" viewBox="0 0 640 512" aria-hidden="true"><use href="#user-icon-user-plus"/></svg>
                <span>${labels.register}</span>
            </a>
        `;

        if (userDropdownMenu) {
            userDropdownMenu.innerHTML = dropdownContent;
        }

        if (mobileUserDropdownMenu) {
            mobileUserDropdownMenu.innerHTML = dropdownContent;
        }
    }

    function enableHoverInteractions() {
        revealMenus();
        document.documentElement.classList.add('allow-user-dropdown-hover');
    }

    function handleDropdownFocusIn() {
        revealMenus();
    }

    // Defer CSS :hover until the user actually moves the pointer, so a reload
    // with the cursor already over the icon does not flash the open/active state.
    // Menus also stay [hidden] until first interaction to avoid a pre-CSS flash.
    window.addEventListener('pointermove', enableHoverInteractions, {once: true, passive: true});
    userAuthNav?.addEventListener('focusin', handleDropdownFocusIn);
    mobileUserIcon?.addEventListener('focusin', handleDropdownFocusIn);

    checkUserStatus();
    window.addEventListener('focus', checkUserStatus);

    teardown = function() {
        authRequestId++;
        window.removeEventListener('focus', checkUserStatus);
        window.removeEventListener('pointermove', enableHoverInteractions);
        userAuthNav?.removeEventListener('focusin', handleDropdownFocusIn);
        mobileUserIcon?.removeEventListener('focusin', handleDropdownFocusIn);
        document.documentElement.classList.remove('allow-user-dropdown-hover');
        initialized = false;
        setupStarted = false;
        menusRevealed = false;
        teardown = function() {};
    };
}
